import { Helmet } from "react-helmet"
import {
	Avatar,
	Box,
	Card,
	CardContent,
	Divider,
	Grid,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography
} from "@mui/material"
import {
	useNavigate,
	useSearchParams,
} from "react-router-dom"
import React, { useEffect, useMemo, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { makeStyles } from "@mui/styles"
import { useTranslation } from "react-i18next"
import moment from "moment"

import Search from "@mui/icons-material/Search"
import { ReactComponent as Nodata } from "../../../../assets/icons/nodata.svg"
import { ReactComponent as ChevronExpandDown } from "../../../../assets/icons/chevron-expand-down.svg"
import { ReactComponent as ChevronExpandUp } from "../../../../assets/icons/chevron-expand-up.svg"

import CustomPagination from "../../../../components/CustomPagination"

import {
	getAchievementDashboard
} from "../../../../actions/managerDashboard"

import ApexCharts from "react-apexcharts"
import debounce from 'lodash.debounce'
import * as Util from "../../../../services/global.service"

const AchievementManagerDashboard = (props) => {
	const { t, i18n } = useTranslation()
	const managerDashboardState = useSelector((state) => state.ManagerDashboard)
	const dispatch = useDispatch()
	const [filtersQuery, setFiltersQuery] = React.useState({
		query: "",
		reqData: {
			organization_id: [],
			cycle_id: ""
		}
	})
	let chartOptions = {
		series: [
			{
				name: "Goals",
				data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
			},
			{
				name: "Competencies",
				data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
			},
			{
				name: "GoalsAndCompetencies",
				data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
			},
			{
				name: "Others",
				data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
			},
		],
		options: {
			chart: {
				type: "bar",
				height: "100px",
				stacked: true,
				toolbar: {
					show: false,
				},
			},
			colors: ["#FA9349", "#74C1ED", "#F7B254", "#31B09B"],
			plotOptions: {
				bar: {
					horizontal: false,
					borderRadius: 5,
					columnWidth: "30%",
				},
			},
			dataLabels: {
				enabled: false,
			},
			grid: {
				show: false,
			},
			stroke: {
				width: 1,
				colors: ["#fff"],
			},
			title: {
				show: false,
			},
			xaxis: {
				categories: [
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
					"MM-YY",
				],
				labels: {
					formatter: function (val) {
						return val
					},
				},
				axisBorder: {
					show: false,
				},
				axisTicks: {
					show: false,
				},
			},
			yaxis: {
				title: {
					text: undefined,
				},
				tickAmount: 1,
				labels: {
					formatter: function (val) {
						return val.toFixed(0)
					}
				}
			},
			tooltip: {
				enabled: true,
				enabledOnSeries: undefined,
				shared: true,
				followCursor: false,
				intersect: false,
				inverseOrder: false,
				custom: undefined,
				fillSeriesColor: false,
				style: {
					fontSize: "16px",
					fontFamily: "Kanit",
					fontWeight: 300,
				},
				onDatasetHover: {
					highlightDataSeries: false,
				},
				x: {
					show: true,
					formatter: function (value, opts) {
						let total = opts.w.globals.stackedSeriesTotals[opts.dataPointIndex]
						return `${t("label.total")}: ` + Math.trunc(total)
					},
				},
				y: {
					formatter: undefined,
					title: {
						formatter: (seriesName) => t(`label.${seriesName}`) + ":",
					},
				},
				z: {
					formatter: undefined,
					title: "Size: ",
				},
				marker: {
					show: true,
				},
				items: {
					display: "flex",
				},
				fixed: {
					enabled: false,
					position: "topRight",
					offsetX: 0,
					offsetY: 0,
				},
			},
			fill: {
				opacity: 1,
			},
			legend: {
				show: false,
				position: "top",
				horizontalAlign: "right",
			},
		},
	}
	const [chart, setChart] = React.useState(chartOptions)
	const [chartRawData, setChartRawData] = React.useState({})
	const [summaryItems, setSummaryItem] = React.useState({
		total: 0,
		goal: 0,
		competency: 0,
		goalAndCompetency: 0,
		others: 0,
	})
	const [filteredList, setFilteredList] = React.useState([])
	const [list, setList] = React.useState([])
	const [currentCycle, setCurrentCycle] = React.useState({})
	const [perPage, setPerPage] = React.useState(50)
	const [currentPage, setCurrentPage] = React.useState(1)
	const [totalPage, setTotalPage] = React.useState(1)
	const columnWidths = [
		"calc(10% - 12px)",
		"calc(40% - 12px)",
		"calc(10% - 12px)",
		"calc(10% - 12px)",
		"calc(14% - 12px)",
		"calc(20% - 12px)",
		"calc(10% - 12px)"
	]
	const [searchFilters, setSearchFilters] = React.useState("")

	const handleSearchFilters = (val, filters) => {
		setSearchFilters(val)
		debouncSearch(val, filters)
	}

	const debouncSearch = useCallback(debounce((word, oldFilters) => {
		let filters = {
			...oldFilters,
			...{
				reqData: {
					...oldFilters.reqData,
					"word": word
				}
			}
		}
		setFiltersQuery(filters)
	}, 2000), [])

	const useStyles = makeStyles({
		flexPaper: {
			flex: 1,
			minWidth: 350,
			boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)!important",
			borderRadius: "10px!important",
		},
		paperRoot: {
			display: "flex",
			width: "100%",
			flexWrap: "wrap",
		}
	})
	const classes = useStyles()
	const styles = {
		link: {
			color: "#5DA196",
			cursor: "pointer",
		},
		end: {
			color: "#A3A3A3",
		},
	}

	const loadSummaryAndList = () => {
		dispatch(getAchievementDashboard(filtersQuery)).then((resp) => {
			if (resp?.achievement_summary) setSummaryData(resp?.achievement_summary)
			if (resp?.total_employee) dispatch({
				type: "SET_TOTAL_EMPLOYEE_DASHBOARD_MANAGER",
				payload: resp?.total_employee ?? 0
			})
			if (resp?.achievement_by_employee) {
				let employees = resp.achievement_by_employee.map((item, index) => {
					return {
						...item,
						number: index + 1
					}
				})
				setList(employees)
				setFilteredList(employees)
				setChartRawData(resp?.achievement_summary?.chart)
			}
		})
	}

	const setSummaryData = (params) => {
		const data = {
			total: params?.total ?? 0,
			goal: params?.goal ?? 0,
			competency: params?.competency ?? 0,
			goalAndCompetency: params?.goal_and_competency ?? 0,
			others: params?.others ?? 0,
		}
		setSummaryItem(data)
	}

	const mapThaiMonthName = (month) => {
		let result = ""
		switch (month) {
			case "Jan":
				result = "ม.ค."
				break
			case "Feb":
				result = "ก.พ."
				break
			case "Mar":
				result = "มี.ค."
				break
			case "Apr":
				result = "เม.ย."
				break
			case "May":
				result = "พ.ค."
				break
			case "Jun":
				result = "มิ.ย."
				break
			case "Jul":
				result = "ก.ค."
				break
			case "Aug":
				result = "ส.ค."
				break
			case "Sep":
				result = "ก.ย."
				break
			case "Oct":
				result = "ต.ค."
				break
			case "Nov":
				result = "พ.ย."
				break
			case "Dec":
				result = "ธ.ค."
				break
			default:
				break
		}
		return result
	}


	const handlePerPageChange = (event) => {
		setPerPage(event.target.value)
	}

	const handleSetCurrentPage = (value) => {
		const getSelectedPage = value.filter(
			(item) => item.type === "page" && item.selected
		)
		setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1)
	}

	const handleTotalPage = () => {
		const totalPage =
			Math.ceil(filteredList.length / perPage) === 0
				? 1
				: Math.ceil(filteredList.length / perPage)
		setTotalPage(totalPage)
	}

	let sortData = (key, direction) => {
		let result = [...filteredList]
		result = result.sort(function (b, a) {
			if (direction) {
				if (a[key] > b[key]) {
					return -1;
				} else {
					return 1;
				}
			} else {
				if (a[key] < b[key]) {
					return -1;
				} else {
					return 1;
				}
			}
		})
		setFilteredList(result)
	}

	const currentData = useMemo(() => {
		handleTotalPage()
		const firstPageIndex = (currentPage - 1) * perPage
		const lastPageIndex = firstPageIndex + perPage
		return filteredList.slice(firstPageIndex, lastPageIndex)
	}, [filteredList, currentPage, perPage])

	// useEffect
	useEffect(() => {
		let query = []
		if (managerDashboardState?.filtersChecked && managerDashboardState?.cycleData?.serial && managerDashboardState?.cycleData?.serial !== "") {
			let reqData = {
				organization_id: [],
				cycle_id: ""
			}
			let orgIds = managerDashboardState?.filtersChecked?.map(filter => {
				return `${filter}`
			})
			if (orgIds.length > 0) {
				reqData.organization_id = orgIds
				reqData.cycle_id = managerDashboardState.cycleData.serial
				setCurrentCycle(managerDashboardState.cycleData)
				setFiltersQuery({ query, reqData })
			}
		}
	}, [managerDashboardState.filtersChecked])

	useEffect(() => {
		if (filtersQuery?.reqData?.cycle_id !== "" && filtersQuery?.reqData?.organization_id?.length > 0) {
			loadSummaryAndList()
		}
	}, [filtersQuery])

	useEffect(() => {
		i18n.language === "th-TH" ? moment.locale('th') : moment.locale('en')
		let monthRangeStart = moment()
		let monthRangeEnd = moment()
		let monthDiff = monthRangeEnd.diff(monthRangeStart, 'months')
		if (currentCycle) {
			monthRangeStart = currentCycle?.start_date ?? false ? moment(currentCycle.start_date, 'YYYY-MM-DD') : moment()
			monthRangeEnd = currentCycle?.end_date ?? false ? moment(currentCycle.end_date, 'YYYY-MM-DD') : moment()
			monthDiff = monthRangeEnd.diff(monthRangeStart, 'months')
		}

		let initSeries = []
		const categories = []
		let months = []
		for (let i = 0; i <= monthDiff; i++) {
			initSeries.push(0)
			let date = monthRangeStart
			if (i === 0) {
				date.add(0, "months")
			} else {
				date.add(1, "months")
			}
			let month = date.format("MM")
			let monthName = date.format("MMM")
			let year = date.format("YYYY")

			months.push({
				monthNo: month,
				monthName: monthName,
				year: parseInt(year),
			})

			if (i18n.language === "th-TH") {
				year = parseInt(year) + 543
			}
			categories.push(`${monthName}-${year}`)
		}
		let seriesData = {
			goals: {
				name: "Goals",
				data: [...initSeries],
			},
			competencies: {
				name: "Competencies",
				data: [...initSeries],
			},
			goalsAndCompetencies: {
				name: "GoalsAndCompetencies",
				data: [...initSeries],
			},
			others: {
				name: "Others",
				data: [...initSeries],
			},
		}
		// set data
		months.forEach((month, index) => {
			let tmpChartRawData = JSON.parse(JSON.stringify(chartRawData))

			tmpChartRawData?.competency?.forEach(competency => {
				if (String(competency.month) === String(month.monthNo) && String(competency.year) === String(month.year)) {
					seriesData.competencies.data[index] = seriesData.competencies.data[index] + (competency?.count ?? 0)
				}
			})
			tmpChartRawData?.goal?.forEach(goal => {
				if (String(goal.month) === String(month.monthNo) && String(goal.year) === String(month.year)) {
					seriesData.goals.data[index] = seriesData.goals.data[index] + (goal?.count ?? 0)
				}
			})
			tmpChartRawData?.goal_and_competency?.forEach(gac => {
				if (String(gac.month) === String(month.monthNo) && String(gac.year) === String(month.year)) {
					seriesData.goalsAndCompetencies.data[index] = seriesData.goalsAndCompetencies.data[index] + (gac?.count ?? 0)
				}
			})
			tmpChartRawData?.others?.forEach(other => {
				if (String(other.month) === String(month.monthNo) && String(other.year) === String(month.year)) {
					seriesData.others.data[index] = seriesData.others.data[index] + (other?.count ?? 0)
				}
			})
		})
		let tempChartData = {
			...chart,
			...{
				series: [
					seriesData.goals,
					seriesData.competencies,
					seriesData.goalsAndCompetencies,
					seriesData.others,
				],
			},
		}
		tempChartData.options = {
			...tempChartData.options,
			...{
				xaxis: {
					...tempChartData.options.xaxis,
					categories: categories,
				},
			},
		}
		setChart(tempChartData)
	}, [chartRawData, currentCycle])

	// useEffect(() => {
	// 	let resultList = JSON.parse(JSON.stringify(list))
	// 	if (searchFilters !== "") {
	// 		resultList = resultList.filter(item => item.name.toLowerCase().includes(searchFilters.toLowerCase()))
	// 	}
	// 	setFilteredList(resultList)
	// }, [searchFilters])
	// end useEffect

	return (
		<>
			<Box id="back-to-top-anchor"> </Box>
			<Grid container columnSpacing={4} mt={4}>
				<Grid item sm md xs={12}>
					<Card sx={{ maxHeight: "95px" }}>
						<CardContent>
							<Grid container spacing={2}>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									textAlign="left"
								>
									<Typography
										fontSize={14}
										lineHeight="21px"
										fontWeight="400"
										color="#6B6B6B"
									>
										{t("achievement.label.totalAchievements")}
									</Typography>
								</Grid>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<Typography
										fontSize={36}
										color={summaryItems.total > 0 ? "primary" : "#C4C4C4"}
										fontWeight="600"
									>
										{summaryItems.total}
									</Typography>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid item sm md xs={12}>
					<Card sx={{ maxHeight: "95px" }}>
						<CardContent>
							<Grid container spacing={2}>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									textAlign="left"
								>
									<Typography
										fontSize={14}
										lineHeight="21px"
										fontWeight="400"
										color="#6B6B6B"
									>
										{t("achievement.label.goalAchievements")}
									</Typography>
								</Grid>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<Typography
										fontSize={36}
										color={summaryItems.goal > 0 ? "#FA9349" : "#C4C4C4"}
										fontWeight="600"
									>
										{summaryItems.goal}
									</Typography>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid item sm md xs={12}>
					<Card sx={{ maxHeight: "95px" }}>
						<CardContent>
							<Grid container spacing={2}>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									textAlign="left"
								>
									<Typography
										fontSize={14}
										lineHeight="21px"
										fontWeight="400"
										color="#6B6B6B"
									>
										{t("achievement.label.competencyAchievements")}
									</Typography>
								</Grid>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<Typography
										fontSize={36}
										color={
											summaryItems.competency > 0 ? "#74C1ED" : "#C4C4C4"
										}
										fontWeight="600"
									>
										{summaryItems.competency}
									</Typography>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid item sm md xs={12}>
					<Card sx={{ maxHeight: "95px" }}>
						<CardContent>
							<Grid container spacing={2}>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									textAlign="left"
								>
									<Typography
										fontSize={14}
										lineHeight="21px"
										fontWeight="400"
										color="#6B6B6B"
									>
										{t("achievement.label.goalAndCompetencyAchievements")}
									</Typography>
								</Grid>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<Typography
										fontSize={36}
										color={
											summaryItems.goalAndCompetency > 0
												? "#F7B254"
												: "#C4C4C4"
										}
										fontWeight="600"
									>
										{summaryItems.goalAndCompetency}
									</Typography>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid item sm md xs={12}>
					<Card sx={{ maxHeight: "95px" }}>
						<CardContent>
							<Grid container spacing={2}>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									textAlign="left"
								>
									<Typography
										fontSize={14}
										lineHeight="21px"
										fontWeight="400"
										color="#6B6B6B"
										sx={{ whitespace: "pre-line" }}
									>
										{t("achievement.label.othersAchievements")}
									</Typography>
								</Grid>
								<Grid
									item
									lg={6}
									xs={12}
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<Typography
										fontSize={36}
										color={
											summaryItems.others > 0 ? "primary" : "#C4C4C4"
										}
										fontWeight="600"
									>
										{summaryItems.others}
									</Typography>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			<Grid container spacing={4} mt={0} mb={4}>
				<Grid item xs={12}>
					<Card sx={{ boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)", borderRadius: "10px" }}>
						<CardContent sx={{ p: "20px" }}>
							<Grid container spacing={1} p={0}>
								<Grid item xs={12}>
									<Box display="flex" alignItems="center">
										<Typography fontSize="24px" fontWeight="600">
											{t("achievement.label.achievementOverview")}
										</Typography>
									</Box>
								</Grid>
								<Grid item xs={12} mt={0}>
									<Box
										sx={{
											display: "flex",
											flexFlow: "row wrap",
											margin: "0 0 0 auto",
											maxWidth: "max-content",
										}}
									>
										{chart.series.length > 0
											? chart.series.map((loRow, index) => (
												<Box
													key={index}
													display="flex"
													alignItems="center"
												>
													<Box
														sx={{ width: "15px", height: "15px" }}
														mr="2.5"
													>
														<div
															className="bar-legend-square"
															style={{
																backgroundColor:
																	chart.options.colors[index],
															}}
														></div>
													</Box>
													<Box
														sx={{
															width: "max-content",
															overflow: "hidden",
															"white-space": "nowrap",
															"text-overflow": "ellipsis",
														}}
														px="10px"
													>
														<Typography
															variant="label"
															style={styles.barLegend}
															fontWeight={300}
														>
															{" "}
															{t(`label.${loRow.name}`)}
														</Typography>
													</Box>
												</Box>
											))
											: null}
									</Box>
									<Box
										sx={{
											".apexcharts-tooltip-marker": { borderRadius: "2px" },
											".apexcharts-tooltip-y-group > span": {
												fontWeight: "400",
											},
											".apexcharts-tooltip": {
												background: "#F7F7F7",
												fontWeight: "400",
											},
											height: "500",
											".apexcharts-legend-text": {
												color: "#A3A3A3 !important",
											},
											".apexcharts-text": {
												color: "#A3A3A3 !important",
												fill: "#A3A3A3  !important",
											},
											".apexcharts-tooltip-title": {
												background: "#5DA196 !important",
												color: "#FFFFFF",
												fontWeight: "400",
											},
											height: "370px"
										}}
									>
										<ApexCharts
											options={chart.options}
											series={chart.series}
											type={chart.options.chart.type}
											height="100%"
										></ApexCharts>
									</Box>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* TABLE */}
			<Grid container spacing={2}>
				<Grid item xs>
					<Box className={classes.paperRoot}>
						<Paper className={classes.flexPaper}>
							<Grid container spacing={2} px="20px" mt="20px" mb="20px">
								<Grid item >
									<Typography fontSize={24} fontWeight="600">{t("dashboardManager.label.recordedAchievementRank")}</Typography>
								</Grid>
								<Grid item ml="auto">
									<TextField
										id="search-table"
										fullWidth
										placeholder={t("dashboardManager.label.searchPlaceholderAchievement")}
										size="small"
										sx={{
											background: "#fff",
											width: "400px",
											height: "45px",
										}}
										value={searchFilters}
										onInput={e => handleSearchFilters(e.target.value, filtersQuery)}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<Search style={{ color: "#C4C4C4" }} />
												</InputAdornment>
											)
										}}
									/>
								</Grid>
							</Grid>
							{/* TABLE HEADER */}
							<Box px="20px">
								<Box
									px="25px"
									py="19px"
									sx={{ display: "flex", flexDirection: "row" }}
								>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											width: columnWidths[0],
										}}
										pr="15px"
									>
										<Typography
											fontSize={16}
											lineHeight="23.96px"
											color="#A0A0A0"
											fontWeight="500"
											sx={{
												whiteSpace: "nowrap",
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												maxWidth: "max-content",
												mr: "10px",
											}}
										>
											{t("dashboardManager.label.no")}
										</Typography>
										<Typography
											component="span"
											style={{
												flexDirection: "column",
												justifyContent: "center",
												display: "flex",
											}}
										>
											<ChevronExpandUp
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("number", true)}
											/>
											<ChevronExpandDown
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("number", false)}
											/>
										</Typography>
									</Box>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											width: columnWidths[1],
										}}
										pr="15px"
									>
										<Typography
											fontSize={16}
											lineHeight="23.96px"
											color="#A0A0A0"
											fontWeight="500"
											sx={{
												whiteSpace: "nowrap",
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												maxWidth: "max-content",
												mr: "10px",
											}}
										>
											{t("dashboardManager.label.name")}
										</Typography>
										<Typography
											component="span"
											style={{
												flexDirection: "column",
												justifyContent: "center",
												display: "flex",
											}}
										>
										</Typography>
										<Typography
											component="span"
											style={{
												flexDirection: "column",
												justifyContent: "center",
												display: "flex",
											}}
										>
											<ChevronExpandUp
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("name", true)}
											/>
											<ChevronExpandDown
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("name", false)}
											/>
										</Typography>
									</Box>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											width: columnWidths[2],
										}}
										pr="15px"
									>
										<Typography
											fontSize={16}
											lineHeight="23.96px"
											color="#A0A0A0"
											fontWeight="500"
											sx={{
												whiteSpace: "nowrap",
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												maxWidth: "max-content",
												mr: "10px",
											}}
										>
											{t("dashboardManager.label.total")}
										</Typography>
										<Typography
											component="span"
											style={{
												flexDirection: "column",
												justifyContent: "center",
												display: "flex",
											}}
										>
											<ChevronExpandUp
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("total", true)}
											/>
											<ChevronExpandDown
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("total", false)}
											/>
										</Typography>
									</Box>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											width: columnWidths[3],
										}}
										pr="15px"
									>
										<Typography
											fontSize={16}
											lineHeight="23.96px"
											color="#A0A0A0"
											fontWeight="500"
											sx={{
												whiteSpace: "nowrap",
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												maxWidth: "max-content",
												mr: "10px",
											}}
										>
											{t("dashboardManager.label.goals")}
										</Typography>
										<Typography
											component="span"
											style={{
												flexDirection: "column",
												justifyContent: "center",
												display: "flex",
											}}
										>
											<ChevronExpandUp
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("goal", true)}
											/>
											<ChevronExpandDown
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("goal", false)}
											/>
										</Typography>
									</Box>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											width: columnWidths[4],
										}}
										pr="15px"
									>
										<Typography
											fontSize={16}
											lineHeight="23.96px"
											color="#A0A0A0"
											fontWeight="500"
											sx={{
												whiteSpace: "nowrap",
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												maxWidth: "max-content",
												mr: "10px",
											}}
										>
											{t("dashboardManager.label.competencies")}
										</Typography>
										<Typography
											component="span"
											style={{
												flexDirection: "column",
												justifyContent: "center",
												display: "flex",
											}}
										>
											<ChevronExpandUp
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("competency", true)}
											/>
											<ChevronExpandDown
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("competency", false)}
											/>
										</Typography>
									</Box>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											width: columnWidths[5],
										}}
										pr="15px"
									>
										<Typography
											fontSize={16}
											lineHeight="23.96px"
											color="#A0A0A0"
											fontWeight="500"
											sx={{
												whiteSpace: "nowrap",
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												maxWidth: "max-content",
												mr: "10px",
											}}
										>
											{t("dashboardManager.label.goalsAndCompetencies")}
										</Typography>
										<Typography
											component="span"
											style={{
												flexDirection: "column",
												justifyContent: "center",
												display: "flex",
											}}
										>
											<ChevronExpandUp
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("goal_and_competency", true)}
											/>
											<ChevronExpandDown
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("goal_and_competency", false)}
											/>
										</Typography>
									</Box>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											width: columnWidths[6],
										}}
										pr="15px"
									>
										<Typography
											fontSize={16}
											lineHeight="23.96px"
											color="#A0A0A0"
											fontWeight="500"
											sx={{
												whiteSpace: "nowrap",
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												maxWidth: "max-content",
												mr: "10px",
											}}
										>
											{t("dashboardManager.label.others")}
										</Typography>
										<Typography
											component="span"
											sx={{
												flexDirection: "column",
												justifyContent: "center",
												display: "flex",
											}}
										>
											<ChevronExpandUp
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("others", true)}
											/>
											<ChevronExpandDown
												style={{ cursor: "pointer" }}
												onClick={(e) => sortData("others", false)}
											/>
										</Typography>
									</Box>
								</Box>
								<Divider width="100%" />
							</Box>
							{currentData &&
								currentData.length > 0 ? (
								currentData.map((row, index) => (
									<Box px="20px" key={`emp-${index}`}>
										<Box
											sx={{
												width: "100%",
												position: "relative",
												py: 1
											}}
										>
											<Box
												sx={{
													height: "96px",
													background: "#FFF",
													width: "100%",
													display: "flex",
													alignItems: "center",
												}}
												px="25px"
											>
												<Box sx={{ width: columnWidths[0] }} pr="15px">
													<Typography
														fontSize={16}
														lineHeight="23.96px"
														fontWeight="400"
														sx={{
															whiteSpace: "nowrap",
															width: "100%",
															overflow: "hidden",
															textOverflow: "ellipsis",
														}}
													>
														{row?.number ?? "-"}
													</Typography>
												</Box>
												<Box sx={{ width: columnWidths[1] }} pr="15px">
													<Grid container spacing={2} flexWrap="nowrap">
														<Grid item display="flex" alignItems="center">
															<Avatar
																src={row?.photo}
															/>
														</Grid>
														<Grid item>
															<Grid container>
																<Grid item display="flex" alignItems="center">
																	<Typography fontSize="18px" fontWeight="500">
																		{row?.name ?? "-"}
																	</Typography>
																</Grid>
															</Grid>
															<Grid container>
																<Grid item display="flex" alignItems="center">
																	<Typography fontSize="16px" fontWeight="400" color="#AAAAAA">
																		{row?.position ?? "-"}  ({row?.organization ?? "-"})
																	</Typography>
																</Grid>
															</Grid>
														</Grid>
													</Grid>
												</Box>
												<Box
													sx={{
														width: columnWidths[2],
														display: "flex",
														justifyContent: "flex-start",
														alignItems: "center",
													}}
													pr="15px"
												>
													<Typography
														fontWeight="400"
														fontSize="16px"
													>
														{row?.total ?? 0}
													</Typography>
												</Box>
												<Box sx={{ width: columnWidths[3] }} pr="15px">
													<Typography
														fontWeight="400"
														fontSize="16px"
													>
														{row?.goal ?? 0}
													</Typography>
												</Box>
												<Box sx={{ width: columnWidths[4] }} pr="15px">
													<Typography
														fontWeight="400"
														fontSize={16}
														lineHeight="23.92px"
														sx={{
															width: "100%"
														}}
													>
														{row?.competency ?? 0}
													</Typography>
												</Box>
												<Box
													sx={{
														display: "flex",
														flexDirection: "column",
														width: columnWidths[5],
													}}
													pr="15px"
												>
													<Typography
														fontWeight="400"
														fontSize="16px"
														color="#404349"
														sx={{
															width: "100%"
														}}
													>
														{row?.goal_and_competency ?? 0}
													</Typography>
												</Box>
												<Box
													sx={{
														display: "flex",
														flexDirection: "column",
														width: columnWidths[6],
													}}
													pr="15px"
												>
													<Typography
														fontWeight="400"
														fontSize={16}
														sx={{
															width: "100%"
														}}
													>
														{row?.others ?? 0}
													</Typography>
												</Box>
											</Box>
										</Box>
										<Divider width="100%" />
									</Box>
								))
							) : (
								<Box
									sx={{
										pt: "50px",
										pb: "50px",
										textAlign: "center",
									}}
								>
									<Nodata />
									<Typography
										color="#A0A0A0"
										fontWeight="500"
										fontSize={20}
										lineHeight="30px"
									>
										{t("label.noData")}
									</Typography>
								</Box>
							)}
							<Grid container mt={1} spacing={2} px="20px" pb="24px">
								<Grid item xs sm md display="flex" alignItems="center">
									<Box mr={1}>
										<Typography variant="s14_fw400">
											{t("label.showMaxItems")}
										</Typography>
									</Box>
									<Select
										id="pagination"
										variant="outlined"
										value={perPage}
										onChange={handlePerPageChange}
										displayEmpty
										size="small"
										className="white-bg"
									>
										<MenuItem value={50}>
											<Typography style={{ fontWeight: "300" }}>50</Typography>
										</MenuItem>
										<MenuItem value={100}>
											<Typography style={{ fontWeight: "300" }}>100</Typography>
										</MenuItem>
										<MenuItem value={200}>
											<Typography style={{ fontWeight: "300" }}>200</Typography>
										</MenuItem>
									</Select>
								</Grid>
								<Grid item display="flex" alignItems="center" ml="auto">
									<Typography variant="s14_fw400">
										{t("label.page")} {currentPage} {t("label.from")} {totalPage}
									</Typography>
								</Grid>
								<Grid item ml="auto" display="flex" alignItems="center">
									<CustomPagination
										setCurrentPage={handleSetCurrentPage}
										selected={currentPage}
										count={totalPage}
									></CustomPagination>
								</Grid>
							</Grid>
						</Paper>
					</Box>
				</Grid>
			</Grid>
            {Util.ScrollToTop()}
		</>
	)
}
export default AchievementManagerDashboard
